import { Switch } from "@mui/material";
import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";

import { customNameComparator } from "../../../../../helpers/coappDataGridUtils";
import { getUserColumnFilterValue } from "../../../../../helpers/datagridFiltering";
import CoAppDataGridDateTimeCell from "../../../../global/components/datagrid/CoAppDataGridDateTimeCell";
import CoAppDataGridListCell from "../../../../global/components/datagrid/CoAppDataGridListCell";
import CoAppDataGridNameCell from "../../../../global/components/datagrid/CoAppDataGridNameCell";
import ThreeDotMenu from "../../../../global/components/ThreeDotMenu";

export const roleUserColumns = ["actions", "user", "roles", "groups", "lastExtensionHeartbeat", "createdAt", "status"];

export interface RoleUserRow {
	id: string | number;
	active: boolean;
	isCoAppSuperAdmin?: boolean;
	roles?: string[];
	groups?: string[];
	lastHeartbeat?: string | null;
	createdAt?: string | null;
	user?: {
		firstname?: string;
		lastname?: string;
		email?: string;
	};
	[key: string]: unknown;
}

export const getRoleUsersColumns = (
	organizationSsoStatus: boolean,
	navigationItemReferences: { roleNavItemIndex: number } & Record<string, number>,
	viewUserPage: (params: GridRenderCellParams<RoleUserRow>, navIndex: number, roleId: string, stateName: string) => void,
	roleId: string,
	stateName: string,
	removeUserFromRole: (userId: RoleUserRow["id"]) => void,
	handleUserStatusToggle: (userId: RoleUserRow["id"], state: "false" | "true") => void,
	currentUserIsCoAppSuperAdmin: boolean
): GridColDef<RoleUserRow>[] => [
	{
		editable: false,
		field: "user",
		valueGetter: (value, row) => getUserColumnFilterValue(value, row),
		sortComparator: (v1, v2, _param1, _param2) => customNameComparator(v1, v2) ?? 0,
		flex: 1,
		headerName: "User",
		pinnable: false,
		sortable: true,
		renderCell: (params: GridRenderCellParams<RoleUserRow>) => (
			<CoAppDataGridNameCell item={params.row} isUser={true} isCoAppSuperAdmin={params.row.isCoAppSuperAdmin} />
		),
	},
	{
		editable: false,
		field: "roles",
		sortable: false,
		flex: 1,
		headerName: "Roles",
		pinnable: false,
		renderCell: (params: GridRenderCellParams<RoleUserRow, string[] | undefined>) => (
			<CoAppDataGridListCell items={params.value} resource="Roles" isCoAppSuperAdmin={params.row.isCoAppSuperAdmin} />
		),
	},
	{
		disableColumnMenu: true,
		editable: false,
		field: "groups",
		filterable: false,
		flex: 1,
		headerName: "Groups",
		pinnable: false,
		sortable: false,
		renderCell: (params: GridRenderCellParams<RoleUserRow, string[] | undefined>) => (
			<CoAppDataGridListCell items={params.value} resource="Groups" />
		),
	},
	{
		editable: false,
		field: "lastHeartbeat",
		width: 200,
		headerName: "Last heartbeat",
		pinnable: false,
		valueGetter: (value) => {
			if (!value) {
				return null;
			}
			return new Date(value as string);
		},
		sortable: true,
		renderCell: (params: GridRenderCellParams<RoleUserRow, Date | null>) => (
			<CoAppDataGridDateTimeCell value={params.value} />
		),
	},
	{
		editable: false,
		field: "createdAt",
		width: 200,
		headerName: "Created",
		pinnable: false,
		valueGetter: (value) => {
			if (!value) {
				return null;
			}
			return new Date(value as string);
		},
		sortable: true,
		renderCell: (params: GridRenderCellParams<RoleUserRow, Date | null>) => (
			<CoAppDataGridDateTimeCell value={params.value} />
		),
	},
	{
		editable: false,
		field: "active",
		width: 90,
		headerName: "Status",
		sortable: true,
		pinnable: false,
		renderCell: (params: GridRenderCellParams<RoleUserRow>) => (
			<Switch
						checked={params.row.active}
						disabled={organizationSsoStatus || params.row.isCoAppSuperAdmin}
						onChange={() => handleUserStatusToggle(params.row.id, params.row.active === true ? "false" : "true")}
					/>
		),
	},
	{
		field: "actions",
		flex: .1,
		sortable: false,
		type: "actions",
		renderCell: (params: GridRenderCellParams<RoleUserRow>) => (
			<div>
						{
							organizationSsoStatus ?
								<ThreeDotMenu
									options={[
										{
											name: "View User",
											optionClickHandler: () => {
												viewUserPage(params, navigationItemReferences.roleNavItemIndex, roleId, stateName);
											},
										},
									]}
									isCoAppSuperAdmin={params.row.isCoAppSuperAdmin}
								/>
								:
								<ThreeDotMenu
									options={[
										...((!params.row.isCoAppSuperAdmin || currentUserIsCoAppSuperAdmin) ? [{
											name: "Remove From Role",
											optionClickHandler: () => {
												removeUserFromRole(params.row.id);
											},
										}] : []),
										{
											name: "View User",
											optionClickHandler: () => {
												viewUserPage(params, navigationItemReferences.roleNavItemIndex, roleId, stateName);
											},
										},
									]}
									isCoAppSuperAdmin={params.row.isCoAppSuperAdmin}
								/>
						}
					</div>
		),
	}
];
